import type { EntityType } from './entity-access.model';

export type EntityFamilyPkType = 'uuid' | 'text';

export interface EntityFamilyOps {
  read: string;
  write: string;
  manage: string | null;
}

export interface EntityFamilyDefinition {
  entityType: EntityType;
  permissionPrefix: string;
  table: string;
  pkType: EntityFamilyPkType;
  softDelete: boolean;
  enforced: boolean;
  ops: EntityFamilyOps;
  label: string;
  grantablePermissions: readonly string[];
}

export const ENTITY_FAMILIES: Record<EntityType, EntityFamilyDefinition> = {
  person: {
    entityType: 'person',
    permissionPrefix: 'employees:',
    table: 'core.persons',
    pkType: 'uuid',
    softDelete: true,
    enforced: true,
    ops: { read: 'employees:read', write: 'employees:update', manage: 'core:persons:access:manage' },
    label: 'Person',
    grantablePermissions: [
      'employees:read',
      'employees:update',
      'employees:address:read',
      'employees:address:update',
      'employees:contacts:read',
      'employees:contacts:update',
      'employees:skills:read',
      'employees:skills:update',
      'employees:teams:read',
      'employees:teams:update',
      'organisation:assignments:update',
      'timetracking:periods:read',
      'timetracking:periods:update',
      'plan-instances:read',
      'plan-instances:update',
      'timetracking:manual-bookings:read',
      'timetracking:manual-bookings:update',
      'employees:balances:read',
      'employees:balances:update',
      'timetracking:absences:read',
      'timetracking:absences:update',
    ],
  },
  team: {
    entityType: 'team',
    permissionPrefix: 'teams:',
    table: 'core.teams',
    pkType: 'text',
    softDelete: false,
    enforced: true,
    ops: { read: 'teams:read', write: 'teams:update', manage: 'core:teams:access:manage' },
    label: 'Team',
    grantablePermissions: ['teams:read', 'teams:update'],
  },
  department: {
    entityType: 'department',
    permissionPrefix: 'departments:',
    table: 'core.departments',
    pkType: 'text',
    softDelete: false,
    enforced: true,
    ops: { read: 'departments:read', write: 'departments:update', manage: 'core:departments:access:manage' },
    label: 'Department',
    grantablePermissions: ['departments:read', 'departments:update'],
  },
  calendar: {
    entityType: 'calendar',
    permissionPrefix: 'calendars:',
    table: 'calendar.calendars',
    pkType: 'uuid',
    softDelete: false,
    enforced: true,
    // write is create, not update — calendars have no update permission
    ops: { read: 'calendars:read', write: 'calendars:create', manage: 'calendar:access:manage' },
    label: 'Calendar',
    grantablePermissions: ['calendars:read'],
  },
  location: {
    entityType: 'location',
    permissionPrefix: 'locations:',
    table: 'core.locations',
    pkType: 'uuid',
    softDelete: false,
    enforced: false,
    // no access:manage permission seeded yet — server rejects scoped until enforcement lands
    ops: { read: 'locations:read', write: 'locations:update', manage: null },
    label: 'Location',
    grantablePermissions: ['locations:read', 'locations:update'],
  },
  skill: {
    entityType: 'skill',
    permissionPrefix: 'skills:',
    table: 'core.skills',
    pkType: 'text',
    softDelete: false,
    enforced: false,
    ops: { read: 'skills:read', write: 'skills:update', manage: null },
    label: 'Skill',
    grantablePermissions: ['skills:read', 'skills:update'],
  },
  plan: {
    entityType: 'plan',
    permissionPrefix: 'plans:',
    table: 'planning.plans',
    pkType: 'text',
    softDelete: true,
    enforced: false,
    ops: { read: 'plans:read', write: 'plans:update', manage: null },
    label: 'Plan',
    grantablePermissions: ['plans:read', 'plans:update'],
  },
};

export const ENTITY_FAMILY_LIST: readonly EntityFamilyDefinition[] = Object.values(ENTITY_FAMILIES);

export function getEntityFamily(entityType: EntityType): EntityFamilyDefinition {
  return ENTITY_FAMILIES[entityType];
}
